import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, BellRing, Bot, BriefcaseBusiness, CalendarDays, Check, Clock3,
  Brain, ClipboardList, CornerDownLeft, ExternalLink, Loader2, MessageSquareText, Mic, Send,
  ShieldCheck, Square, UserRound, WalletCards, X, ListChecks, TrendingUp,
  Copy, Link2, Settings2, UsersRound,
} from 'lucide-react';
import { toast } from 'sonner';

import api from '@/api';
import { Sidebar } from '@/components/Sidebar';
import { AuditPanel, MemoryPanel } from '@/components/assistant/AssistantControlPanels';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney } from '@/lib/currency';


const STARTERS = [
  'Покажи моё расписание на завтра',
  'Найди клиентов по теме тревожность',
  'Сделай финансовую сводку за этот месяц',
];

const MAX_VOICE_DURATION_MS = 30000;
const VOICE_LIMIT_MESSAGE = 'Лимит записи — 30 секунд. Более длинные голосовые сообщения не обрабатываются. Запишите команду короче.';


const formatTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};


const formatDateTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};


const STATUS_LABELS = {
  active: 'Активен', paused: 'На паузе', archived: 'В архиве',
  pending: 'Ожидает', pending_admin: 'У администратора', pending_doctor: 'У специалиста',
  confirmed: 'Подтверждена', waiting: 'Ожидает приёма', in_progress: 'Идёт приём',
  completed: 'Завершена', paid: 'Оплачена', cancelled: 'Отменена',
  open: 'Открыта', done: 'Выполнена', failed: 'Ошибка', sent: 'Доставлено',
  unavailable: 'Нет канала', disabled: 'Отключено', retrying: 'Повторная попытка',
};


const CHANNEL_LABELS = {
  telegram: 'Telegram', email: 'Email', sms: 'SMS', none: 'Не выбран',
};


const PIPELINE_TRIGGER_LABELS = {
  manual: 'Вручную', daily: 'Ежедневно', weekly: 'Еженедельно', monthly: 'Ежемесячно',
};


const pipelineStageSummary = (stage) => {
  const value = stage?.data || {};
  if (stage?.kind === 'appointments.summary') return `${value.total || 0} записей, отмен ${value.cancelled || 0}`;
  if (stage?.kind === 'appointments.compare') return `Изменение записей: ${Number(value.appointments_delta || 0) > 0 ? '+' : ''}${value.appointments_delta || 0}`;
  if (stage?.kind === 'appointments.cancellations') return `${value.cancelled || 0} отмен, ${value.cancellation_rate || 0}%`;
  if (stage?.kind === 'schedule.capacity') return `${value.appointments || 0} записей, ${value.booked_minutes || 0} мин.`;
  if (stage?.kind === 'finance.forecast') {
    const forecasts = value.forecasts_by_currency || [];
    return forecasts.length > 0
      ? `Прогноз: ${forecasts.map(item => formatMoney(item.projected_revenue, item.currency)).join(' · ')}`
      : `Прогноз: ${formatMoney(value.projected_revenue, value.currency)}`;
  }
  if (stage?.kind === 'recommendations') return (value.items || []).join(' ');
  if (stage?.kind?.startsWith('delivery.')) return ({
    preview: 'Предварительный просмотр', delivered: 'Результат доставлен',
    telegram_not_connected: 'Telegram не подключён', failed: 'Не удалось доставить результат',
    pending: 'Ожидает доставки',
  }[value.mode] || 'Результат сохранён');
  return '';
};


const ResultTitle = ({ icon: Icon, children, detail }) => (
  <div className="flex flex-wrap items-center gap-2">
    <Icon size={15} style={{ color: 'var(--app-accent)' }} aria-hidden="true" />
    <h3 className="text-xs font-semibold" style={{ color: 'var(--app-text)' }}>{children}</h3>
    {detail && <span className="text-[11px]" style={{ color: 'var(--app-text-muted)' }}>{detail}</span>}
  </div>
);


const DataTable = ({ columns, rows, getKey }) => (
  <div className="mt-2 overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--app-border)' }}>
    <table className="w-full min-w-[520px] border-collapse text-left text-xs">
      <thead style={{ background: 'var(--app-bg)', color: 'var(--app-text-muted)' }}>
        <tr>{columns.map((column) => <th key={column.key} className="px-3 py-2 font-medium">{column.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr
            key={getKey?.(row, index) || row.id || index}
            style={{ borderTop: '1px solid var(--app-border-subtle)' }}
          >
            {columns.map((column, columnIndex) => (
              <td key={column.key} className="px-3 py-2.5 align-top" style={{ color: columnIndex === 0 ? 'var(--app-text)' : 'var(--app-text-secondary)' }}>
                {column.render ? column.render(row) : (row[column.key] || '—')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);


function ToolResultView({ result, onNavigate, onUseSlot }) {
  if (!result || result.ok === false) return null;
  const data = result.data || {};
  const practiceCurrency = data.currency || data.service?.currency || data.services?.[0]?.currency || 'RUB';
  const practiceMoney = (value) => formatMoney(value, practiceCurrency);
  const moneyTotals = (items, field) => {
    const visible = (items || []).filter(item => Number(item[field] || 0) !== 0);
    return visible.length > 0
      ? visible.map(item => formatMoney(item[field], item.currency)).join(' · ')
      : formatMoney(0, practiceCurrency);
  };
  const sections = [];
  const clients = Array.isArray(data.clients) ? data.clients : [];
  const appointments = Array.isArray(data.appointments)
    ? data.appointments
    : (data.appointment ? [data.appointment] : []);
  const services = Array.isArray(data.services) ? data.services : (data.service ? [data.service] : []);

  if (data.client) {
    const client = data.client;
    sections.push(
      <section key="client">
        <ResultTitle icon={UserRound} detail={STATUS_LABELS[client.status] || client.status}>Клиент</ResultTitle>
        <button
          type="button"
          onClick={() => client.id && onNavigate(`/patients?client=${client.id}`)}
          className="mt-2 w-full rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2"
          style={{ background: 'var(--app-bg)' }}
        >
          <span className="block text-sm font-medium" style={{ color: 'var(--app-text)' }}>{client.name}</span>
          <span className="mt-1 block text-xs" style={{ color: 'var(--app-text-muted)' }}>
            {[client.phone, client.email].filter(Boolean).join(' · ') || 'Контакты не указаны'}
          </span>
          {(client.topics || []).length > 0 && (
            <span className="mt-2 flex flex-wrap gap-1.5">
              {client.topics.map((topic) => (
                <span key={topic} className="rounded-full px-2 py-0.5 text-[11px]" style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}>{topic}</span>
              ))}
            </span>
          )}
          {(client.balance !== undefined || client.debt) && (
            <span className="mt-2 block text-xs" style={{ color: client.debt ? '#f87171' : 'var(--app-text-secondary)' }}>
              {client.debt ? `Долг: ${practiceMoney(client.debt)}` : `Баланс: ${practiceMoney(client.balance)}`}
            </span>
          )}
        </button>
      </section>,
    );
  }

  if (clients.length > 0) {
    sections.push(
      <section key="clients">
        <ResultTitle icon={UserRound} detail={`${clients.length}`}>Клиенты</ResultTitle>
        <DataTable
          rows={clients}
          columns={[
            { key: 'name', label: 'Имя' },
            { key: 'phone', label: 'Телефон' },
            { key: 'topics', label: 'Темы', render: (row) => (row.topics || []).join(', ') || '—' },
          ]}
        />
      </section>,
    );
  }

  if (Array.isArray(data.specialists) && data.specialists.length > 0) {
    sections.push(
      <section key="specialists">
        <ResultTitle icon={UserRound} detail={`${data.specialists.length}`}>Специалисты</ResultTitle>
        <DataTable rows={data.specialists} columns={[
          { key: 'name', label: 'Имя' },
          { key: 'specialization', label: 'Специализация' },
          { key: 'status', label: 'Статус', render: (row) => row.status === 'disabled' ? 'Отключён' : 'Активен' },
        ]} />
      </section>,
    );
  }

  const teamMembers = Array.isArray(data.team_members)
    ? data.team_members
    : (data.team_member ? [data.team_member] : []);
  if (teamMembers.length > 0) {
    sections.push(
      <section key="team-members">
        <ResultTitle icon={UsersRound} detail={`${teamMembers.length}`}>Команда</ResultTitle>
        <DataTable rows={teamMembers} columns={[
          { key: 'name', label: 'Сотрудник' },
          { key: 'role_label', label: 'Роль', render: (row) => row.role_label || (row.role === 'admin' ? 'Администратор' : 'Специалист') },
          { key: 'email', label: 'Email' },
          { key: 'status', label: 'Статус', render: (row) => ({ active: 'Активен', invited: 'Приглашён', disabled: 'Отключён' }[row.status] || row.status) },
        ]} />
      </section>,
    );
  }

  if (data.booking_settings) {
    const settings = data.booking_settings;
    const bookable = (settings.services || []).filter((service) => service.bookable);
    sections.push(
      <section key="booking-settings">
        <ResultTitle icon={Settings2}>Онлайн-запись</ResultTitle>
        <div className="mt-2 divide-y text-xs" style={{ borderColor: 'var(--app-border-subtle)' }}>
          <div className="flex items-center justify-between gap-4 py-2">
            <span style={{ color: 'var(--app-text-muted)' }}>Приём новых клиентов</span>
            <span className="font-medium" style={{ color: settings.accepting_clients ? 'var(--app-accent)' : 'var(--app-text-secondary)' }}>
              {settings.accepting_clients ? 'Включён' : 'Выключен'}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4 py-2">
            <span style={{ color: 'var(--app-text-muted)' }}>Минимальный срок</span>
            <span style={{ color: 'var(--app-text)' }}>{settings.min_booking_lead_hours || 0} ч.</span>
          </div>
          <div className="flex items-center justify-between gap-4 py-2">
            <span style={{ color: 'var(--app-text-muted)' }}>Проверка клиента по коду</span>
            <span style={{ color: 'var(--app-text)' }}>{settings.require_patient_otp ? 'Включена' : 'Выключена'}</span>
          </div>
        </div>
        <div className="mt-3">
          <p className="text-[11px]" style={{ color: 'var(--app-text-muted)' }}>Доступные услуги</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {bookable.length > 0 ? bookable.map((service) => (
              <span key={service.id} className="rounded-full px-2 py-1 text-[11px]" style={{ color: 'var(--app-accent)', background: 'var(--app-accent-muted)' }}>
                {service.name}
              </span>
            )) : <span className="text-xs" style={{ color: 'var(--app-text-secondary)' }}>Нет доступных услуг</span>}
          </div>
        </div>
      </section>,
    );
  }

  if (Array.isArray(data.booking_links)) {
    const linkStatus = { active: 'Активна', used: 'Использована', expired: 'Истекла' };
    sections.push(
      <section key="booking-links">
        <ResultTitle icon={Link2} detail={`${data.booking_links.length}`}>Ссылки онлайн-записи</ResultTitle>
        {data.booking_links.length > 0 ? (
          <div className="mt-2 divide-y" style={{ borderColor: 'var(--app-border-subtle)' }}>
            {data.booking_links.map((link) => (
              <div key={link.id} className="flex items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium" style={{ color: 'var(--app-text)' }}>{link.patient_name}</p>
                  <p className="mt-0.5 text-[11px]" style={{ color: 'var(--app-text-muted)' }}>
                    {link.format_name || 'Любой доступный формат'} · {linkStatus[link.status] || link.status}{link.expires_at ? `, до ${String(link.expires_at).slice(0, 10)}` : ''}
                  </p>
                </div>
                {link.url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 gap-1.5"
                    onClick={() => navigator.clipboard.writeText(link.url)
                      .then(() => toast.success('Ссылка скопирована'))
                      .catch(() => toast.error('Не удалось скопировать ссылку'))}
                  >
                    <Copy size={13} /> Копировать
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : <p className="mt-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>Активных ссылок нет.</p>}
      </section>,
    );
  }

  const subscription = data.subscription;
  if (subscription) {
    sections.push(
      <section key="subscription">
        <ResultTitle icon={WalletCards} detail={subscription.price_monthly === null ? 'По запросу' : `${formatMoney(subscription.price_monthly)} в месяц`}>Тариф «{subscription.name}»</ResultTitle>
        <div className="mt-2 divide-y text-xs" style={{ borderColor: 'var(--app-border-subtle)' }}>
          {[
            ['Клиенты', subscription.usage?.patients, subscription.max_patients],
            ['Администраторы', subscription.usage?.admins, subscription.max_admins],
            ['Специалисты', subscription.usage?.doctors, subscription.max_doctors],
          ].map(([label, current, limit]) => (
            <div key={label} className="flex items-center justify-between gap-4 py-2">
              <span style={{ color: 'var(--app-text-muted)' }}>{label}</span>
              <span style={{ color: 'var(--app-text)' }}>{current || 0} / {limit === null || limit === undefined ? 'без лимита' : limit}</span>
            </div>
          ))}
        </div>
      </section>,
    );
  }

  const modules = Array.isArray(data.modules)
    ? data.modules
    : (Array.isArray(subscription?.modules) ? subscription.modules : null);
  if (modules) {
    sections.push(
      <section key="modules">
        <ResultTitle icon={Settings2} detail={`${modules.filter((item) => item.enabled).length} включено`}>Модули</ResultTitle>
        <div className="mt-2 divide-y" style={{ borderColor: 'var(--app-border-subtle)' }}>
          {modules.map((module) => (
            <div key={module.id} className="flex items-center justify-between gap-4 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-xs font-medium" style={{ color: 'var(--app-text)' }}>{module.name}</p>
                {(module.depends_on || []).length > 0 && (
                  <p className="mt-0.5 text-[11px]" style={{ color: 'var(--app-text-muted)' }}>Зависит от: {(module.depends_on_names || module.depends_on).join(', ')}</p>
                )}
              </div>
              <span
                className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium"
                style={{
                  color: module.enabled ? 'var(--app-accent)' : 'var(--app-text-muted)',
                  background: module.enabled ? 'var(--app-accent-muted)' : 'var(--app-bg)',
                }}
              >
                {module.enabled ? 'Включён' : (module.available_on_plan ? 'Выключен' : 'Не входит в тариф')}
              </span>
            </div>
          ))}
        </div>
      </section>,
    );
  }

  if (data.subscription_change) {
    const change = data.subscription_change;
    const delta = change.monthly_delta;
    sections.push(
      <section key="subscription-change">
        <ResultTitle icon={TrendingUp}>Расчёт смены тарифа</ResultTitle>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>{change.current?.name}</span>
          <span aria-hidden="true" style={{ color: 'var(--app-text-muted)' }}>→</span>
          <span className="text-sm font-semibold" style={{ color: 'var(--app-text)' }}>{change.target?.name}</span>
          <span className="text-xs" style={{ color: Number(delta) > 0 ? 'var(--app-accent)' : 'var(--app-text-secondary)' }}>
            {delta === null || delta === undefined ? 'стоимость по запросу' : `${Number(delta) > 0 ? '+' : ''}${formatMoney(delta)} в месяц`}
          </span>
        </div>
        {(change.features_enabled || []).length > 0 && <p className="mt-2 text-xs" style={{ color: 'var(--app-text-secondary)' }}>Станут доступны: {change.features_enabled.join(', ')}</p>}
        {(change.features_disabled || []).length > 0 && <p className="mt-1 text-xs" style={{ color: 'var(--app-text-secondary)' }}>Станут недоступны: {change.features_disabled.join(', ')}</p>}
        {(change.blockers || []).length > 0 && (
          <ul className="mt-2 space-y-1 text-xs" style={{ color: '#f87171' }}>
            {change.blockers.map((item) => <li key={item}>{item}</li>)}
          </ul>
        )}
      </section>,
    );
  }

  const pipelines = Array.isArray(data.pipelines) ? data.pipelines : (data.pipeline ? [data.pipeline] : null);
  if (pipelines) {
    sections.push(
      <section key="pipelines">
        <ResultTitle icon={ListChecks} detail={`${pipelines.length}`}>Конвейеры</ResultTitle>
        {pipelines.length > 0 ? (
          <div className="mt-2 divide-y" style={{ borderColor: 'var(--app-border-subtle)' }}>
            {pipelines.map((pipeline) => (
              <div key={pipeline.id} className="py-3 first:pt-1 last:pb-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold" style={{ color: 'var(--app-text)' }}>{pipeline.name}</p>
                    <p className="mt-0.5 text-[11px] leading-4" style={{ color: 'var(--app-text-muted)' }}>
                      {PIPELINE_TRIGGER_LABELS[pipeline.trigger] || pipeline.trigger}
                      {pipeline.trigger !== 'manual' ? `, ${pipeline.schedule_time}` : ''}
                      {pipeline.next_run_at ? ` · следующий запуск ${formatDateTime(pipeline.next_run_at)}` : ''}
                    </p>
                  </div>
                  <span className="rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: pipeline.status === 'active' ? 'var(--app-accent-muted)' : 'var(--app-bg)', color: pipeline.status === 'active' ? 'var(--app-accent)' : 'var(--app-text-muted)' }}>
                    {pipeline.status === 'active' ? 'Включён' : 'Черновик'}
                  </span>
                </div>
                {(pipeline.stages || []).length > 0 && (
                  <ol className="mt-2 space-y-1.5">
                    {pipeline.stages.map((stage, index) => (
                      <li key={stage.id || `${pipeline.id}-${index}`} className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--app-text-secondary)' }}>
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px]" style={{ background: 'var(--app-bg)', color: 'var(--app-text-muted)' }}>{index + 1}</span>
                        <span>{stage.title}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            ))}
          </div>
        ) : <p className="mt-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>Конвейеров пока нет.</p>}
      </section>,
    );
  }

  const pipelineRuns = Array.isArray(data.pipeline_runs)
    ? data.pipeline_runs
    : (data.pipeline_run ? [data.pipeline_run] : null);
  if (pipelineRuns) {
    sections.push(
      <section key="pipeline-runs">
        <ResultTitle icon={TrendingUp} detail={`${pipelineRuns.length}`}>Запуски конвейера</ResultTitle>
        {pipelineRuns.length > 0 ? (
          <div className="mt-2 divide-y" style={{ borderColor: 'var(--app-border-subtle)' }}>
            {pipelineRuns.map((run) => (
              <div key={run.id} className="py-3 first:pt-1 last:pb-1">
                <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
                  <span style={{ color: 'var(--app-text-muted)' }}>{formatDateTime(run.started_at)}</span>
                  <span style={{ color: run.status === 'completed' ? 'var(--app-accent)' : '#f87171' }}>{run.status === 'completed' ? 'Завершён' : 'Ошибка'}</span>
                </div>
                {(run.results || []).length > 0 && (
                  <div className="mt-2 space-y-2">
                    {run.results.map((stage, index) => (
                      <div key={stage.id || index} className="grid gap-0.5 sm:grid-cols-[180px_1fr] sm:gap-4">
                        <p className="text-[11px] font-medium" style={{ color: 'var(--app-text)' }}>{stage.title}</p>
                        <p className="text-[11px] leading-4" style={{ color: 'var(--app-text-secondary)' }}>{pipelineStageSummary(stage)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : <p className="mt-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>Запусков пока нет.</p>}
      </section>,
    );
  }

  if (Array.isArray(data.slot_groups)) {
    sections.push(
      <section key="slots">
        <ResultTitle icon={Clock3} detail={data.service?.name}>Свободные окна</ResultTitle>
        {data.slot_groups.length > 0 ? (
          <div className="mt-3 space-y-3">
            {data.slot_groups.map((group) => (
              <div key={group.date} className="grid gap-2 sm:grid-cols-[128px_1fr] sm:items-start">
                <div>
                  <p className="text-xs font-medium" style={{ color: 'var(--app-text)' }}>{group.weekday}</p>
                  <p className="text-[11px]" style={{ color: 'var(--app-text-muted)' }}>{group.date}</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(group.slots || []).map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => onUseSlot(group.date, slot, data.service)}
                      className="rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2"
                      style={{ color: 'var(--app-accent)', borderColor: 'var(--app-border)', background: 'var(--app-bg)' }}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : <p className="mt-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>В выбранном периоде свободных окон нет.</p>}
      </section>,
    );
  }

  if (appointments.length > 0) {
    sections.push(
      <section key="appointments">
        <ResultTitle icon={CalendarDays} detail={`${appointments.length}`}>Записи</ResultTitle>
        <DataTable
          rows={appointments}
          columns={[
            { key: 'date', label: 'Дата', render: (row) => <>{row.date}<br /><span style={{ color: 'var(--app-text-muted)' }}>{row.time}</span></> },
            { key: 'patient_name', label: 'Клиент', render: (row) => row.patient_name || data.client?.name || '—' },
            { key: 'service', label: 'Услуга', render: (row) => row.format_name || row.service || '—' },
            { key: 'status', label: 'Статус', render: (row) => STATUS_LABELS[row.status] || row.status || '—' },
          ]}
        />
      </section>,
    );
  }

  if (services.length > 0 && !Array.isArray(data.slot_groups)) {
    sections.push(
      <section key="services">
        <ResultTitle icon={BriefcaseBusiness} detail={`${services.length}`}>Услуги</ResultTitle>
        <DataTable rows={services} columns={[
          { key: 'name', label: 'Название' },
          { key: 'duration', label: 'Длительность', render: (row) => `${row.duration || 60} мин.` },
          { key: 'price', label: 'Стоимость', render: (row) => formatMoney(row.price, row.currency || practiceCurrency) },
        ]} />
      </section>,
    );
  }

  if (Array.isArray(data.tasks)) {
    sections.push(
      <section key="tasks">
        <ResultTitle icon={ListChecks} detail={`${data.tasks.length}`}>Задачи по клиенту</ResultTitle>
        {data.tasks.length > 0 ? (
          <DataTable rows={data.tasks} columns={[
            { key: 'title', label: 'Задача' },
            { key: 'due_at', label: 'Срок', render: (row) => row.due_at || 'Не задан' },
            { key: 'assignee_name', label: 'Ответственный', render: (row) => row.assignee_name || 'Не указан' },
            { key: 'status', label: 'Статус', render: (row) => STATUS_LABELS[row.status] || row.status || '—' },
          ]} />
        ) : <p className="mt-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>По этому клиенту задач пока нет.</p>}
      </section>,
    );
  }

  if (Array.isArray(data.messages)) {
    sections.push(
      <section key="client-messages">
        <ResultTitle icon={MessageSquareText} detail={`${data.messages.length}`}>Сообщения с клиентом</ResultTitle>
        {data.messages.length > 0 ? (
          <div className="mt-2 divide-y" style={{ borderColor: 'var(--app-border-subtle)' }}>
            {data.messages.map((message, index) => (
              <div key={message.id || index} className="grid gap-1 py-2.5 sm:grid-cols-[150px_1fr] sm:gap-4">
                <div>
                  <p className="text-xs font-medium" style={{ color: 'var(--app-text)' }}>{message.sender_name || 'Система'}</p>
                  <p className="text-[11px]" style={{ color: 'var(--app-text-muted)' }}>{formatTime(message.created_at)}</p>
                </div>
                <div>
                  <p className="whitespace-pre-wrap text-xs leading-5" style={{ color: 'var(--app-text-secondary)' }}>{message.text}</p>
                  {message.delivery?.status && (
                    <p className="mt-1 text-[11px]" style={{ color: 'var(--app-text-muted)' }}>
                      {STATUS_LABELS[message.delivery.status] || message.delivery.status}
                      {message.delivery.channel && message.delivery.channel !== 'none' ? `, ${CHANNEL_LABELS[message.delivery.channel] || message.delivery.channel}` : ''}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : <p className="mt-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>Защищённый чат с клиентом ещё не начат.</p>}
      </section>,
    );
  }

  if (Array.isArray(data.unpaid_appointments)) {
    sections.push(
      <section key="unpaid">
        <ResultTitle icon={WalletCards} detail={moneyTotals(data.totals_by_currency, 'unpaid_total')}>Неоплаченные записи</ResultTitle>
        {data.unpaid_appointments.length > 0 ? (
          <DataTable rows={data.unpaid_appointments} columns={[
            { key: 'date', label: 'Дата', render: (row) => <>{row.date}<br /><span style={{ color: 'var(--app-text-muted)' }}>{row.time}</span></> },
            { key: 'patient_name', label: 'Клиент' },
            { key: 'service', label: 'Услуга' },
            { key: 'amount', label: 'К оплате', render: (row) => formatMoney(row.amount, row.currency || practiceCurrency) },
          ]} />
        ) : <p className="mt-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>Просроченных оплат за период нет.</p>}
      </section>,
    );
  }

  if (Array.isArray(data.specialist_load)) {
    sections.push(
      <section key="specialist-load">
        <ResultTitle icon={UserRound} detail={`${data.specialist_load.length}`}>Нагрузка специалистов</ResultTitle>
        <DataTable rows={data.specialist_load} getKey={(row) => row.doctor_id} columns={[
          { key: 'doctor_name', label: 'Специалист' },
          { key: 'appointments', label: 'Записи', render: (row) => row.appointments || 0 },
          { key: 'booked_hours', label: 'Часы', render: (row) => `${row.booked_hours || 0} ч.` },
          { key: 'cancelled', label: 'Отмены', render: (row) => row.cancelled || 0 },
          { key: 'revenue', label: 'Выручка', render: (row) => {
            const values = (row.revenue_by_currency || []).filter(item => Number(item.amount || 0) !== 0);
            return values.length > 0
              ? values.map(item => <div key={item.currency}>{formatMoney(item.amount, item.currency)}</div>)
              : formatMoney(0, row.currency || practiceCurrency);
          } },
        ]} />
      </section>,
    );
  }

  if (data.period_comparison?.current && data.period_comparison?.previous) {
    const comparison = data.period_comparison;
    const rows = [
      ['income', 'Доход'], ['expenses', 'Расходы'], ['profit', 'Прибыль'], ['average_check', 'Средний чек'],
    ].map(([key, label]) => ({ key, label, current: comparison.current[key], previous: comparison.previous[key], delta: comparison.deltas?.[key] }));
    sections.push(
      <section key="period-comparison">
        <ResultTitle icon={TrendingUp}>Сравнение периодов</ResultTitle>
        <DataTable rows={rows} getKey={(row) => row.key} columns={[
          { key: 'label', label: 'Показатель' },
          { key: 'current', label: `${comparison.current.period?.from || ''}–${comparison.current.period?.to || ''}`, render: (row) => practiceMoney(row.current) },
          { key: 'previous', label: `${comparison.previous.period?.from || ''}–${comparison.previous.period?.to || ''}`, render: (row) => practiceMoney(row.previous) },
          { key: 'delta', label: 'Изменение', render: (row) => <span style={{ color: Number(row.delta?.absolute) >= 0 ? 'var(--app-accent)' : 'var(--app-text-secondary)' }}>{Number(row.delta?.absolute) >= 0 ? '+' : ''}{practiceMoney(row.delta?.absolute)}{row.delta?.percent !== null && row.delta?.percent !== undefined ? ` (${row.delta.percent > 0 ? '+' : ''}${row.delta.percent}%)` : ''}</span> },
        ]} />
      </section>,
    );
  }

  if (data.finance_forecast) {
    const forecast = data.finance_forecast;
    const forecastAmounts = forecast.forecast_by_currency || [];
    const scheduledAmounts = forecast.scheduled_by_currency || [];
    sections.push(
      <section key="forecast">
        <ResultTitle icon={TrendingUp} detail={`${forecast.period?.from || ''}–${forecast.period?.to || ''}`}>Прогноз дохода</ResultTitle>
        <div className="mt-2 grid gap-x-6 gap-y-3 sm:grid-cols-3">
          <div><p className="text-[11px]" style={{ color: 'var(--app-text-muted)' }}>Ориентир</p><p className="mt-0.5 text-sm font-semibold" style={{ color: 'var(--app-text)' }}>{forecastAmounts.length > 0 ? forecastAmounts.map(item => formatMoney(item.amount, item.currency)).join(' · ') : practiceMoney(forecast.forecast_income)}</p></div>
          <div><p className="text-[11px]" style={{ color: 'var(--app-text-muted)' }}>Уже запланировано</p><p className="mt-0.5 text-sm font-semibold" style={{ color: 'var(--app-text)' }}>{scheduledAmounts.length > 0 ? scheduledAmounts.map(item => formatMoney(item.amount, item.currency)).join(' · ') : practiceMoney(forecast.scheduled_income)}</p></div>
          <div><p className="text-[11px]" style={{ color: 'var(--app-text-muted)' }}>Записей в календаре</p><p className="mt-0.5 text-sm font-semibold" style={{ color: 'var(--app-text)' }}>{forecast.scheduled_appointments || 0}</p></div>
        </div>
        {(forecast.assumptions || []).length > 0 && (
          <ul className="mt-3 space-y-1 text-[11px]" style={{ color: 'var(--app-text-muted)' }}>
            {forecast.assumptions.map((item) => <li key={item}>• {item}</li>)}
          </ul>
        )}
      </section>,
    );
  }

  if (Array.isArray(data.notification_templates)) {
    sections.push(
      <section key="notification-templates">
        <ResultTitle icon={BellRing} detail={`${data.notification_templates.length}`}>Шаблоны уведомлений</ResultTitle>
        <div className="mt-2 divide-y" style={{ borderColor: 'var(--app-border-subtle)' }}>
          {data.notification_templates.map((template) => (
            <div key={template.key} className="grid gap-1 py-3 sm:grid-cols-[170px_1fr] sm:gap-5">
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--app-text)' }}>{template.name}</p>
                <p className="text-[11px]" style={{ color: 'var(--app-text-muted)' }}>{template.customized ? 'Изменён' : 'Стандартный'}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium" style={{ color: 'var(--app-text-secondary)' }}>{template.subject}</p>
                <p className="mt-1 whitespace-pre-wrap text-xs leading-5" style={{ color: 'var(--app-text-muted)' }}>{template.message}</p>
                <p className="mt-1 break-words text-[10px]" style={{ color: 'var(--app-text-muted)' }}>
                  {(template.allowed_placeholders || []).map((name) => `{${name}}`).join(' · ')}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>,
    );
  }

  if (Array.isArray(data.delivery_log)) {
    sections.push(
      <section key="delivery-log">
        <ResultTitle icon={BellRing} detail={`${data.delivery_log.length}`}>Журнал доставки</ResultTitle>
        <DataTable rows={data.delivery_log} columns={[
          { key: 'created_at', label: 'Время', render: (row) => <>{row.created_at?.slice(0, 10) || '—'}<br /><span style={{ color: 'var(--app-text-muted)' }}>{formatTime(row.created_at)}</span></> },
          { key: 'subject', label: 'Уведомление' },
          { key: 'channel', label: 'Канал', render: (row) => CHANNEL_LABELS[row.channel] || row.channel || 'Не выбран' },
          { key: 'status', label: 'Статус', render: (row) => STATUS_LABELS[row.status] || row.status || 'Не указан' },
          { key: 'retry_count', label: 'Повторы', render: (row) => row.retry_count || 0 },
        ]} />
      </section>,
    );
  }

  if (Array.isArray(data.schedule)) {
    sections.push(
      <section key="schedule">
        <ResultTitle icon={CalendarDays}>Рабочая неделя</ResultTitle>
        <div className="mt-2 divide-y" style={{ borderColor: 'var(--app-border-subtle)' }}>
          {data.schedule.map((day) => (
            <div key={day.day} className="flex items-center justify-between gap-4 py-2 text-xs">
              <span style={{ color: 'var(--app-text)' }}>{day.label}</span>
              <span style={{ color: day.enabled ? 'var(--app-text-secondary)' : 'var(--app-text-muted)' }}>
                {day.enabled ? `${day.start}–${day.end}` : 'Выходной'}
              </span>
            </div>
          ))}
        </div>
      </section>,
    );
  }

  if (Array.isArray(data.transactions) || Array.isArray(data.notifications)) {
    sections.push(
      <section key="history">
        <ResultTitle icon={WalletCards}>История клиента</ResultTitle>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <div className="rounded-lg px-3 py-2" style={{ background: 'var(--app-bg)' }}>
            <p className="text-[11px]" style={{ color: 'var(--app-text-muted)' }}>Финансовые операции</p>
            <p className="mt-1 text-lg font-semibold" style={{ color: 'var(--app-text)' }}>{data.transactions?.length || 0}</p>
          </div>
          <div className="rounded-lg px-3 py-2" style={{ background: 'var(--app-bg)' }}>
            <p className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--app-text-muted)' }}><BellRing size={12} /> Уведомления</p>
            <p className="mt-1 text-lg font-semibold" style={{ color: 'var(--app-text)' }}>{data.notifications?.length || 0}</p>
          </div>
        </div>
      </section>,
    );
  }

  if (data.facts && data.calculations) {
    const metrics = [
      ['Доход', data.facts.income],
      ['Расходы', data.facts.expenses],
      ['Налог', data.calculations.tax],
      ['Прибыль', data.calculations.profit],
    ];
    sections.push(
      <section key="finance">
        <ResultTitle icon={WalletCards} detail={`${data.facts.period?.from || ''}–${data.facts.period?.to || ''}`}>Финансовая сводка</ResultTitle>
        <div className="mt-2 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-4">
          {metrics.map(([label, value]) => (
            <div key={label}>
              <p className="text-[11px]" style={{ color: 'var(--app-text-muted)' }}>{label}</p>
              <p className="mt-0.5 text-sm font-semibold" style={{ color: 'var(--app-text)' }}>{practiceMoney(value)}</p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px]" style={{ color: 'var(--app-text-muted)' }}>
          Оплаченных записей: {data.facts.paid_appointments || 0} · ставка налога: {data.calculations.tax_rate || 0}%
        </p>
      </section>,
    );
  }

  const notificationSettings = data.settings || (data.hours_before ? data : null);
  if (notificationSettings?.reminder_schedule_hours || notificationSettings?.hours_before) {
    const hours = notificationSettings.reminder_schedule_hours || notificationSettings.hours_before;
    sections.push(
      <section key="notifications">
        <ResultTitle icon={BellRing}>Напоминания</ResultTitle>
        <p className="mt-2 text-xs" style={{ color: 'var(--app-text-secondary)' }}>
          {notificationSettings.patient_notifications_enabled === false || notificationSettings.enabled === false
            ? 'Выключены'
            : `За ${(Array.isArray(hours) ? hours : [hours]).map((value) => `${value} ч.`).join(' и ')}`}
        </p>
      </section>,
    );
  }

  if (!sections.length) return null;
  return (
    <div className="mt-3 space-y-4 rounded-xl border p-3.5" style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
      {sections}
    </div>
  );
}


const encodePcm16 = (chunks, sourceRate, targetRate = 16000) => {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const input = new Float32Array(totalLength);
  let offset = 0;
  chunks.forEach((chunk) => {
    input.set(chunk, offset);
    offset += chunk.length;
  });
  const ratio = sourceRate / targetRate;
  const outputLength = Math.floor(input.length / ratio);
  const output = new Int16Array(outputLength);
  for (let index = 0; index < outputLength; index += 1) {
    const start = Math.floor(index * ratio);
    const end = Math.max(start + 1, Math.floor((index + 1) * ratio));
    let sum = 0;
    for (let cursor = start; cursor < end && cursor < input.length; cursor += 1) sum += input[cursor];
    const sample = Math.max(-1, Math.min(1, sum / (end - start)));
    output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output.buffer;
};


const disposeRecording = (recording) => {
  if (!recording) return;
  window.clearTimeout(recording.timer);
  recording.processor.onaudioprocess = null;
  try { recording.source.disconnect(); } catch {}
  try { recording.processor.disconnect(); } catch {}
  try { recording.sink.disconnect(); } catch {}
  recording.stream.getTracks().forEach((track) => track.stop());
  recording.context.close().catch(() => {});
};


function PendingAction({ action, busy, onConfirm, onCancel }) {
  const preview = action.preview || {};
  const enhanced = action.confirmation_level === 'enhanced';
  return (
    <section
      className="mt-3 rounded-xl border p-4"
      style={{
        background: enhanced ? 'color-mix(in srgb, var(--app-accent) 7%, var(--app-card))' : 'var(--app-bg)',
        borderColor: enhanced ? 'color-mix(in srgb, var(--app-accent) 42%, var(--app-border))' : 'var(--app-border)',
      }}
      aria-label="Действие ожидает подтверждения"
    >
      <div className="flex items-start gap-3">
        <span
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}
        >
          {enhanced ? <ShieldCheck size={17} /> : <Clock3 size={17} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--app-text)' }}>
              {preview.title || 'Подтвердите действие'}
            </h3>
            {enhanced && (
              <span
                className="rounded-full px-2 py-0.5 text-[11px] font-medium"
                style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}
              >
                Важное изменение
              </span>
            )}
          </div>
          {preview.description && (
            <p className="mt-1 text-sm leading-6" style={{ color: 'var(--app-text-secondary)' }}>
              {preview.description}
            </p>
          )}
          {(preview.consequences || []).length > 0 && (
            <ul className="mt-2 space-y-1 text-xs" style={{ color: 'var(--app-text-muted)' }}>
              {preview.consequences.map((item, index) => <li key={index}>• {item}</li>)}
            </ul>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => onConfirm(action.id)}
              disabled={busy}
              className="gap-1.5"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              Подтвердить
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onCancel(action.id)}
              disabled={busy}
              className="gap-1.5"
            >
              <X size={15} /> Отменить
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}


export default function AssistantPage() {
  const navigate = useNavigate();
  const [view, setView] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceWarning, setVoiceWarning] = useState('');
  const [actionBusy, setActionBusy] = useState('');
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const recordingRef = useRef(null);

  const fetchSession = useCallback(async ({ quiet = false } = {}) => {
    try {
      const { data } = await api.get('/assistant/session');
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setPendingActions(Array.isArray(data.pending_actions) ? data.pending_actions : []);
    } catch (error) {
      if (error.response?.status === 401) navigate('/login');
      else if (!quiet) toast.error('Не удалось загрузить диалог');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!document.cookie.includes('pp_csrf')) {
      navigate('/login');
      return undefined;
    }
    fetchSession();
    const timer = window.setInterval(() => fetchSession({ quiet: true }), 3000);
    return () => window.clearInterval(timer);
  }, [fetchSession, navigate]);

  useEffect(() => () => disposeRecording(recordingRef.current), []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, pendingActions.length]);

  const pendingById = useMemo(
    () => new Map(pendingActions.map((action) => [action.id, action])),
    [pendingActions],
  );

  const sendMessage = async (override = '') => {
    const value = (override || text).trim();
    if (!value || sending) return;
    setSending(true);
    setText('');
    try {
      await api.post('/assistant/messages', { text: value });
      await fetchSession({ quiet: true });
      inputRef.current?.focus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ассистент не смог обработать запрос');
      setText(value);
    } finally {
      setSending(false);
    }
  };

  const handleAction = async (actionId, operation) => {
    setActionBusy(actionId);
    try {
      const { data } = await api.post(`/assistant/actions/${actionId}/${operation}`, {});
      toast.success(data.message || (operation === 'confirm' ? 'Действие выполнено' : 'Действие отменено'));
      await fetchSession({ quiet: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось обработать действие');
      await fetchSession({ quiet: true });
    } finally {
      setActionBusy('');
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const useAvailableSlot = (date, time, service) => {
    const servicePart = service?.name ? `, услуга «${service.name}»` : '';
    setText(`Создай запись на ${date} в ${time}${servicePart}. Клиент: `);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const discardOverlongRecording = (current = recordingRef.current) => {
    if (!current) return;
    if (recordingRef.current === current) recordingRef.current = null;
    setRecording(false);
    disposeRecording(current);
    setVoiceWarning(VOICE_LIMIT_MESSAGE);
    toast.warning('Запись остановлена: достигнут лимит 30 секунд.');
  };

  const stopRecording = async () => {
    const current = recordingRef.current;
    if (!current) return;
    if (Date.now() - current.startedAt >= MAX_VOICE_DURATION_MS) {
      discardOverlongRecording(current);
      return;
    }
    recordingRef.current = null;
    setRecording(false);
    disposeRecording(current);
    if (!current.chunks.length) return;
    setTranscribing(true);
    try {
      const pcm = encodePcm16(current.chunks, current.context.sampleRate);
      const form = new FormData();
      form.append('audio', new Blob([pcm], { type: 'application/octet-stream' }), 'voice.pcm');
      const { data } = await api.post('/assistant/transcribe', form);
      setText(data.text || '');
      inputRef.current?.focus();
      toast.success('Речь распознана. Проверьте текст перед отправкой.');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Не удалось распознать речь';
      if (String(detail).includes('30 секунд')) setVoiceWarning(VOICE_LIMIT_MESSAGE);
      toast.error(detail);
    } finally {
      setTranscribing(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Запись голоса не поддерживается этим браузером');
      return;
    }
    try {
      setVoiceWarning('');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const context = new AudioContext();
      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(4096, 1, 1);
      const sink = context.createGain();
      sink.gain.value = 0;
      const chunks = [];
      processor.onaudioprocess = (event) => {
        chunks.push(new Float32Array(event.inputBuffer.getChannelData(0)));
      };
      source.connect(processor);
      processor.connect(sink);
      sink.connect(context.destination);
      const current = {
        stream, context, source, processor, sink, chunks, timer: null, startedAt: Date.now(),
      };
      current.timer = window.setTimeout(
        () => discardOverlongRecording(current),
        MAX_VOICE_DURATION_MS,
      );
      recordingRef.current = current;
      setRecording(true);
    } catch {
      toast.error('Разрешите доступ к микрофону, чтобы записать команду');
    }
  };

  return (
    <div data-testid="assistant-page" className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex min-w-0 flex-1 flex-col pt-14 lg:pt-0" style={{ height: '100vh' }}>
        <header
          className="flex flex-wrap items-center gap-3 border-b px-4 py-3 sm:px-7"
          style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}
        >
          <span
            className="flex h-10 w-10 items-center justify-center rounded-xl"
            style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}
          >
            <Bot size={21} />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold leading-tight" style={{ color: 'var(--app-text)' }}>Ассистент</h1>
            <p className="mt-0.5 truncate text-xs" style={{ color: 'var(--app-text-muted)' }}>
              {view === 'chat' && 'Один диалог в веб-приложении и Telegram'}
              {view === 'memory' && 'Подтверждённые рабочие предпочтения'}
              {view === 'audit' && 'Команды, трактовки и выполненные действия'}
            </p>
          </div>
          <nav aria-label="Разделы ассистента" className="flex w-full gap-1 overflow-x-auto rounded-lg border p-1 sm:w-auto" style={{ borderColor: 'var(--app-border)', background: 'var(--app-bg)' }}>
            {[
              { id: 'chat', label: 'Диалог', icon: MessageSquareText },
              { id: 'memory', label: 'Память', icon: Brain },
              { id: 'audit', label: 'Журнал', icon: ClipboardList },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setView(id)}
                aria-current={view === id ? 'page' : undefined}
                className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 sm:flex-none"
                style={view === id
                  ? { background: 'var(--app-card)', color: 'var(--app-text)', boxShadow: '0 1px 2px rgb(0 0 0 / 0.05)' }
                  : { color: 'var(--app-text-muted)' }}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </nav>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-7 sm:py-8">
            {view === 'memory' ? (
              <MemoryPanel />
            ) : view === 'audit' ? (
              <AuditPanel />
            ) : loading ? (
              <div className="space-y-5" aria-label="Загрузка диалога">
                <Skeleton className="h-20 w-3/4 rounded-xl" />
                <Skeleton className="ml-auto h-14 w-1/2 rounded-xl" />
                <Skeleton className="h-28 w-4/5 rounded-xl" />
              </div>
            ) : messages.length === 0 ? (
              <section className="py-8 sm:py-14">
                <MessageSquareText size={28} style={{ color: 'var(--app-accent)' }} />
                <h2 className="mt-5 text-2xl font-semibold tracking-tight" style={{ color: 'var(--app-text)' }}>
                  Что нужно сделать?
                </h2>
                <p className="mt-2 max-w-xl text-sm leading-6" style={{ color: 'var(--app-text-secondary)' }}>
                  Можно искать клиентов, проверять расписание и готовить изменения. Перед изменением данных ассистент покажет детали и попросит подтверждение.
                </p>
                <div className="mt-7 flex flex-col items-start gap-2">
                  {STARTERS.map((starter) => (
                    <button
                      key={starter}
                      type="button"
                      onClick={() => sendMessage(starter)}
                      className="rounded-lg border px-3.5 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2"
                      style={{ color: 'var(--app-text-secondary)', borderColor: 'var(--app-border)', background: 'var(--app-card)' }}
                    >
                      {starter}
                    </button>
                  ))}
                </div>
              </section>
            ) : (
              <div className="space-y-5" role="log" aria-live="polite">
                {messages.map((message) => {
                  const isUser = message.role === 'user';
                  const activeActions = (message.pending_actions || [])
                    .map((action) => pendingById.get(action.id))
                    .filter(Boolean);
                  const deepLinks = (message.tool_results || [])
                    .flatMap((result) => [result?.data?.external_link, result?.data?.deep_link])
                    .filter((link, index, links) => {
                      const target = link?.url || link?.path;
                      return target && links.findIndex((item) => (item?.url || item?.path) === target) === index;
                    });
                  return (
                    <article key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[92%] sm:max-w-[82%] ${isUser ? 'rounded-2xl rounded-br-md px-4 py-3' : 'w-full'}`}
                        style={isUser ? { background: 'var(--app-accent-muted)', color: 'var(--app-text)' } : {}}
                      >
                        {!isUser && (
                          <div className="mb-1.5 flex items-center gap-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                            <Bot size={14} /> Ассистент
                            {message.channel === 'telegram' && <span>из Telegram</span>}
                          </div>
                        )}
                        {isUser && message.channel === 'telegram' && (
                          <div className="mb-1 text-[10px] font-medium" style={{ color: 'var(--app-text-muted)' }}>Из Telegram</div>
                        )}
                        <p className="whitespace-pre-wrap break-words text-sm leading-6" style={{ color: isUser ? undefined : 'var(--app-text-secondary)' }}>
                          {message.content}
                        </p>
                        {!isUser && (message.tool_results || []).map((result, index) => (
                          <ToolResultView
                            key={result.call_id || `${result.tool || result.entity_type || 'result'}-${index}`}
                            result={result}
                            onNavigate={navigate}
                            onUseSlot={useAvailableSlot}
                          />
                        ))}
                        {deepLinks.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {deepLinks.map((link) => (
                              <button
                                key={link.url || link.path}
                                type="button"
                                onClick={() => link.url ? window.location.assign(link.url) : navigate(link.path)}
                                className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2"
                                style={{ color: 'var(--app-accent)', borderColor: 'var(--app-border)', background: 'var(--app-card)' }}
                              >
                                {link.label || 'Открыть'} <ExternalLink size={12} />
                              </button>
                            ))}
                          </div>
                        )}
                        <time className="mt-1.5 block text-right text-[10px]" style={{ color: 'var(--app-text-muted)' }}>
                          {formatTime(message.created_at)}
                        </time>
                        {activeActions.map((action) => (
                          <PendingAction
                            key={action.id}
                            action={action}
                            busy={actionBusy === action.id}
                            onConfirm={(id) => handleAction(id, 'confirm')}
                            onCancel={(id) => handleAction(id, 'cancel')}
                          />
                        ))}
                      </div>
                    </article>
                  );
                })}
                {sending && (
                  <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--app-text-muted)' }}>
                    <Loader2 size={15} className="animate-spin" /> Проверяю данные
                  </div>
                )}
                <div ref={endRef} />
              </div>
            )}
          </div>
        </div>

        {view === 'chat' && <footer className="border-t px-4 py-3 sm:px-7 sm:py-4" style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
          <div className="mx-auto max-w-3xl">
            {voiceWarning && (
              <div
                role="alert"
                className="mb-2.5 flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm"
                style={{
                  background: 'color-mix(in srgb, var(--app-accent) 9%, var(--app-card))',
                  borderColor: 'color-mix(in srgb, var(--app-accent) 35%, var(--app-border))',
                  color: 'var(--app-text-secondary)',
                }}
              >
                <AlertTriangle size={17} className="mt-0.5 shrink-0" style={{ color: 'var(--app-accent)' }} />
                <span className="min-w-0 flex-1 leading-5">{voiceWarning}</span>
                <button
                  type="button"
                  onClick={() => setVoiceWarning('')}
                  aria-label="Закрыть предупреждение"
                  className="rounded p-0.5 transition-colors hover:bg-black/5 focus:outline-none focus:ring-2"
                  style={{ color: 'var(--app-text-muted)' }}
                >
                  <X size={15} />
                </button>
              </div>
            )}
            <div className="flex items-end gap-2 rounded-xl border p-2" style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)' }}>
              <textarea
                ref={inputRef}
                value={text}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                maxLength={8000}
                placeholder="Напишите запрос"
                aria-label="Сообщение ассистенту"
                disabled={sending}
                className="max-h-32 min-h-10 flex-1 resize-none bg-transparent px-2 py-2 text-sm outline-none disabled:opacity-60"
                style={{ color: 'var(--app-text)' }}
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={recording ? stopRecording : startRecording}
                disabled={sending || transcribing}
                aria-label={recording ? 'Остановить запись' : 'Записать голосовую команду'}
                className={`h-10 w-10 shrink-0 ${recording ? 'text-red-400' : ''}`}
              >
                {transcribing ? <Loader2 size={17} className="animate-spin" /> : recording ? <Square size={15} fill="currentColor" /> : <Mic size={18} />}
              </Button>
              <Button
                size="icon"
                onClick={() => sendMessage()}
                disabled={!text.trim() || sending}
                aria-label="Отправить сообщение"
                className="h-10 w-10 shrink-0"
              >
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
              </Button>
            </div>
            <p className="mt-1.5 flex items-center gap-1 text-[11px]" style={{ color: recording ? '#f87171' : 'var(--app-text-muted)' }}>
              {recording ? <><span className="h-1.5 w-1.5 rounded-full bg-red-400" /> Идёт запись, максимум 30 секунд</> : <><CornerDownLeft size={11} /> Enter, отправить. Shift + Enter, новая строка.</>}
            </p>
          </div>
        </footer>}
      </main>
    </div>
  );
}
